import axios from 'axios';
import { HttpStatusCode, NotificationType, Methods } from '../config';
import addNotification from './addNotification';
import { endSession, getSessionGeneration, isCurrentSession, redirectToLogin } from './authSession';

const apiUrl = process.env.REACT_APP_API_URL;

const refreshTokenBlacklist = ['auth/login', 'auth/logout', 'auth/refresh'];

let refreshPromise = null;
let refreshGeneration = null;

const shouldCallRefreshToken = (url) => {
  return url ? !refreshTokenBlacklist.some((path) => url.includes(path)) : false;
};

const isLogoutRequest = (config) => config.url?.includes('auth/logout');
const sessionCancelled = () => new axios.CanceledError('Session ended');

const refreshAccessToken = async (generation) => {
  if (!isCurrentSession(generation)) throw sessionCancelled();
  if (!refreshPromise || refreshGeneration !== generation) {
    refreshGeneration = generation;
    const pending = refreshAuthToken(generation).finally(() => {
      if (refreshPromise === pending) refreshPromise = null;
    });
    refreshPromise = pending;
  }
  const token = await refreshPromise;
  if (!isCurrentSession(generation)) throw sessionCancelled();
  saveToken(token, generation);
  return token;
};

const axiosApiInstance = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

axiosApiInstance.interceptors.request.use(
  async (config) => {
    config.sessionGeneration ??= getSessionGeneration();
    if (!isLogoutRequest(config) && !isCurrentSession(config.sessionGeneration)) throw sessionCancelled();
    let token = getToken();

    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['Content-Type'] = config.formData ? 'multipart/form-data' : 'application/json';
    }

    if (!token && shouldCallRefreshToken(config.url)) {
      token = await refreshAccessToken(config.sessionGeneration);
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosApiInstance.interceptors.response.use(
  (response) => {
    if (!isLogoutRequest(response.config) && !isCurrentSession(response.config.sessionGeneration))
      throw sessionCancelled();
    return response;
  },
  async (error) => {
    if (
      error.code === 'ERR_CANCELED' ||
      error.name === 'CanceledError' ||
      error.name === 'AbortError' ||
      error.message === 'canceled'
    ) {
      return Promise.reject('Request canceled');
    }

    if (error.config && !isLogoutRequest(error.config) && !isCurrentSession(error.config.sessionGeneration)) {
      return Promise.reject(sessionCancelled());
    }

    if (!error.response && error.message) {
      addNotification({
        content: error.message,
        type: NotificationType.ERROR,
      });
    }

    if (error.response && error.config) {
      const { status, data } = error.response;
      const callRefreshToken = shouldCallRefreshToken(error.config.url);

      if (status !== HttpStatusCode.Unauthorized || (status === HttpStatusCode.Unauthorized && !callRefreshToken)) {
        if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
          const reader = new FileReader();
          reader.readAsText(error.response.data);
          reader.onload = () => {
            const jsonData = JSON.parse(reader.result);
            addNotification({
              content: jsonData.error.message,
              type: NotificationType.ERROR,
            });
          };
        }

        if (data?.error?.message) {
          addNotification({
            content: data.error.message,
            type: NotificationType.ERROR,
          });
        }
      }

      if (status === HttpStatusCode.Unauthorized && callRefreshToken) {
        if (error.config.authRetried) {
          endSession();
          redirectToLogin();
          return Promise.reject(error);
        }
        error.config.authRetried = true;
        try {
          const token = await refreshAccessToken(error.config.sessionGeneration);
          error.config.headers['Authorization'] = `Bearer ${token}`;
          return axiosApiInstance.request(error.config);
        } catch (e) {
          return Promise.reject(e);
        }
      }
    }

    return Promise.reject(error);
  }
);

export const isAxiosError = (e) => {
  return Boolean(e?.isAxiosError);
};

const request = async ({
  resource,
  method = Methods.GET,
  transformResponse,
  headers,
  data,
  formData,
  responseType,
  signal,
  ...requestConfig
}) => {
  const url = `/${resource}`;

  const {
    data: response,
    headers: respHeader,
    config,
  } = await axiosApiInstance({
    method,
    url,
    headers,
    data,
    formData,
    responseType,
    signal,
    ...requestConfig,
  });
  if (responseType === 'blob') {
    const contentDisposition = respHeader['content-disposition'];
    const fln = contentDisposition?.match(/filename="([^"]+)"/);
    if (fln) response.filename = decodeURIComponent(fln[1]);
    return response;
  }

  if (response.token) {
    saveToken(response.token, config.sessionGeneration);
    return { ...response.data, token: response.token };
  }
  return response.data;
};

export default request;

export const saveToken = (token, generation = getSessionGeneration()) => {
  if (isCurrentSession(generation)) localStorage.setItem('token', token);
};

const getToken = () => {
  return localStorage.getItem('token');
};

const refreshAuthToken = async (generation) => {
  try {
    const response = await axiosApiInstance.get('/auth/refresh', { sessionGeneration: generation });
    if (!response.data.token) throw new Error('Missing refresh response token');
    return response.data.token;
  } catch (error) {
    if (isCurrentSession(generation)) {
      endSession();
      redirectToLogin();
    }
    throw error;
  }
};
